'use client'

import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppError } from '@/lib/errors/appError'
import { getErrorCopy } from '@/lib/errors/messages'
import { cn } from '@/lib/utils'

interface QueryErrorStateProps {
  error: unknown
  onRetry: () => void
  compact?: boolean
}

/**
 * The inline counterpart to `ErrorBoundary`/`ErrorFallback` (`src/components/errors/`) — for a
 * *query* error (an expected API failure: 404, 422, a network blip), not a render-time crash.
 * Rendered directly in the component that made the call, with its own retry action; never routed
 * through an `ErrorBoundary` (`frontend-code-standards.md` §4).
 */
export function QueryErrorState({ error, onRetry, compact = false }: QueryErrorStateProps) {
  const appError = AppError.fromUnknown(error)
  const copy = getErrorCopy(appError, 'component')

  return (
    <div
      className={cn(
        'border-border bg-card flex flex-col items-center justify-center gap-2 rounded-xl border p-6 text-center',
        compact ? 'min-h-24' : 'min-h-40',
      )}
      role="alert"
    >
      <AlertTriangle className="text-destructive size-5" aria-hidden />
      <p className="text-foreground text-sm font-medium">{copy.title}</p>
      <p className="text-muted-foreground text-xs">{copy.description}</p>
      {appError.requestId ? (
        <p className="text-muted-foreground text-xs">Reference: {appError.requestId}</p>
      ) : null}
      <Button size="sm" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}
